import { Image, Music, Video } from "lucide-react";
import type React from "react";
import type { Message } from "@/features/agent";
import { ApprovalCard } from "./ApprovalCard";
import { BatchApprovalPanel } from "./BatchApprovalPanel";
import { getCredits } from "./credit";
import { isImageTask, isMusicTask, isVideoTask } from "./validators";

export interface MessageApprovalPanelProps {
  message: Pick<Message, "id" | "role" | "content" | "timestamp" | "error" | "checkpoint">;
  isLast?: boolean;
}

export const MessageApprovalPanel: React.FC<MessageApprovalPanelProps> = ({ message, isLast }) => {
  if (!message.checkpoint?.length) return null;
  if (message.checkpoint?.length > 1) {
    return (
      <BatchApprovalPanel messageId={message.id} checkpoint={message.checkpoint} isLast={isLast} />
    );
  }
  const task = message.checkpoint[0];
  const status = isLast ? "pending" : task.approved ? "approved" : "rejected";
  const credits = getCredits(task);

  if (isImageTask(task)) {
    return (
      <ApprovalCard
        messageId={message.id}
        icon={<Image size={16} className="text-slate-400" />}
        label="AI Image"
        meta={task.ratio}
        prompt={task.prompt}
        credits={credits}
        status={status}
      />
    );
  }

  if (isVideoTask(task)) {
    return (
      <ApprovalCard
        messageId={message.id}
        icon={<Video size={16} className="text-slate-400" />}
        label="AI Video"
        meta={`${task.ratio} · ${task.duration}s`}
        prompt={task.prompt}
        credits={credits}
        status={status}
      />
    );
  }

  if (isMusicTask(task)) {
    return (
      <ApprovalCard
        messageId={message.id}
        icon={<Music size={16} className="text-slate-400" />}
        label="AI Music"
        meta="30s"
        prompt={task.prompt}
        credits={credits}
        status={status}
      />
    );
  }

  return null;
};
